import { processObject } from "../../../lib/storageObjectManager";
import { appStorageApi } from "../../config/apiConfig";
import {
  StorageObjectValue,
  StorageScope,
  TObjectShareRequest,
  TObjectShareResponse,
  TObjectShareUpdateRequest,
} from "./types/service_types";
import {
  StorageObject,
  ResponseStorageObject,
  PostStorageObject,
} from "./types/object_types";
import { RagItem, RagItemFront, RagObjectRequest } from "./types/rag_types";

export const getAllStorageObjects = <T extends StorageObjectValue>(
  service: string,
): Promise<StorageObject<T>[]> => {
  return appStorageApi
    .get(`/objects/service/${service}?view=hierarchy`, {
      withCredentials: false,
    })
    .then((res) => {
      const dataWithoutJson: StorageObject<T>[] = res.data.objects.map(
        (object: ResponseStorageObject) => {
          const finalObject = {
            ...object,
            children: object.children?.filter((child) => child !== null),
          };
          return processObject(finalObject);
        },
      );

      return dataWithoutJson;
    });
};

export const getStorageObjectById = <T extends StorageObjectValue>(
  service: string,
  id: string,
  signal?: AbortSignal,
): Promise<StorageObject<T>> => {
  return appStorageApi
    .get(`/objects/service/${service}/${id}`, {
      withCredentials: false,
      signal,
    })
    .then((res) => ({
      ...res.data,
      value: JSON.parse(res.data.value),
    }));
};

export const createStorageObject = <T extends StorageObjectValue>(
  postObj: PostStorageObject,
  service: string,
): Promise<StorageObject<T>> => {
  return appStorageApi
    .post(`/objects/service/${service}`, postObj, {
      withCredentials: false,
    })
    .then((res) => {
      return {
        ...res.data,
        id: String(res.data.id),
        value: JSON.parse(res.data.value),
      };
    });
};

export const newUpdateStorageObject = (
  id: string,
  postObj: PostStorageObject,
  service: string,
  callId?: string,
) => {
  return appStorageApi
    .post(`/objects/service/${service}/${id}`, postObj, {
      withCredentials: false,
    })
    .then((response) => {
      const { object_date, user_ref, ...rest } = response.data;
      const value = JSON.parse(response.data.value);
      return {
        ...rest,
        id: String(rest.id),
        value: value,
      };
    });
};

export const updateStorageObject = <T extends StorageObjectValue>(
  storageObject: StorageObject<T>,
  service: string,
) => {
  const id = storageObject.id as string;
  const postObj: PostStorageObject = {
    friendly_name: storageObject.friendly_name,
    value: JSON.stringify(storageObject.value),
  };
  return appStorageApi
    .post(`/objects/service/${service}/${id}`, postObj)
    .then((response) => response.data);
};

export const deleteStorageObject = (id: string, service: string) => {
  return appStorageApi
    .delete(`/objects/service/${service}/${id}`, {
      withCredentials: false,
    })
    .then((res) => res.data);
};

export const addChildToObject = (
  childId: string,
  parentId: string,
  service: string,
) => {
  const reqBody = {
    child_id: childId,
  };
  return appStorageApi
    .post(`/objects/service/${service}/${parentId}/has_child`, reqBody, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const deleteChildObject = (childId: string | null, parentId: string) => {
  return appStorageApi
    .delete(`/objects/relation/${parentId}/has_child/${childId}`, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const newDeleteChildObject = (
  childId: string | null,
  parentId: string,
  service: StorageScope,
) => {
  return appStorageApi
    .delete(`/objects/relation/${parentId}/has_child/${childId}`, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const getObjectsShare = (): Promise<TObjectShareResponse[]> => {
  return appStorageApi
    .get(`/objects_share/bulk`, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const createObjectShare = (
  postObject: TObjectShareRequest,
  service: StorageScope,
): Promise<TObjectShareResponse> => {
  return appStorageApi
    .post(`/objects_share/${service}`, postObject, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const updateObjectShare = (
  putObject: TObjectShareUpdateRequest,
  id: number,
): Promise<TObjectShareResponse> => {
  return appStorageApi
    .put(`/objects_share?id=${id}`, putObject, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const deleteObjectShare = (id: number) => {
  return appStorageApi
    .delete(`/objects_share?id=${id}`, {
      withCredentials: false,
    })
    .then((res) => {
      return res.data;
    });
};

export const getRagObject = <T extends StorageObjectValue>(
  text: string,
  service: string,
): Promise<RagItemFront<T>[]> => {
  const postObj: RagObjectRequest = {
    text: text,
    limit: 10,
  };
  return appStorageApi
    .post(`/objects/rag/${service}`, postObj)
    .then((response) => {
      const ragResultWithoutJson = response.data.rag_result.map(
        (item: RagItem) => ({
          ...item,
          value: JSON.parse(item.value),
        }),
      );
      return ragResultWithoutJson;
    });
};
